import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import classnames from 'classnames';
import {actions, select} from './connectors';
import {conciseSizeName, groupSizes} from '~/utils/sizeHelpers';
import {turnTimeInWords} from '~/utils/turnTimeHelpers';
import {formatDate} from '~/utils/date';
import renderNonBreakingHypenString from '~/utils/renderNonBreakingHypenString';
import {normalizeId} from 'mms_client_js';
import Color from '~/data/records/Color';
// constants
import remoteServices from '~/constants/RemoteServices';
import {SHIPPING_BOX, SIZING} from '~/constants/ResponsiveImageConstants';
import {
  HOOD_PRINT,
  LEG_PRINT,
  SLEEVE_PRINT
} from '~/constants/AdditionalPrintAreasConstants';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
// components
import ContentCard from '~/components/ContentCard';
import Fetch from '~/utils/components/Fetch';
import PopupLink from '~/shared/components/PopupLink';
import DetailsToggle from './DetailsToggle';
import ImageViewer from './ImageViewer';
import {RUSH, STANDARD} from '~/constants/ShippingConstants';
import withRouter, {routerPropTypes} from '~/router/withRouter';
import {Typography} from '@customink/pigment-react';

import './index.scss';

const propTypes = {
  colorSizes: PropTypes.array,
  handleAdditionalPrintAreasClick: PropTypes.func,
  isDisabled: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  largeBreakPoint: PropTypes.bool,
  meta: PropTypes.shape({
    productSummary: PropTypes.func,
    productSummaryCarousel: PropTypes.func
  }),
  product: PropTypes.shape({
    id: PropTypes.string,
    isFetching: PropTypes.bool
  }),
  router: routerPropTypes,
  // analytics
  selectedColor: PropTypes.instanceOf(Color)
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class ProductSummary extends Component {
  static displayName = 'ProductSummary';

  static propTypes = propTypes;

  state = {
    product: null,
    selectedColor: null
  };

  UNSAFE_componentWillMount() {
    this.setProduct(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setProduct(nextProps);
  }

  setProduct(props) {
    const {product, selectedColor} = props;
    if (product && !product.isFetching) {
      this.setState({product, selectedColor});
    }
  }

  decorationDescription(product) {
    if (product.has_singles_enabled_colors) {
      return 'Screen printing or high-quality digital printing';
    }
    if (
      product.decoration_method === 'screen' ||
      product.decoration_method === 'transfer'
    ) {
      return 'Screen Printing';
    }
    if (product.decoration_method === 'embroidery') {
      return 'Embroidery';
    }
    if (product.decoration_method === 'laser engraved') {
      return null;
    }
  }

  handleChangeProduct = () => this.props.router.navigate('/changeProduct');

  handleSelectColor = () => this.props.router.navigate('/productColor');

  handleDetailsAnalytics = (section, expanded) => {
    if (!this.props.meta) return;
    const event = expanded ? 'open' : 'close';
    this.props.meta.productSummary({section, event});
  };

  renderListItems(...args) {
    const lineItems = args
      .join(' ')
      .split(/\s*\*\s*/)
      .filter(string => string !== '')
      .map((arg, index) => <li key={index}>{arg.trim()}</li>);

    return <ul>{lineItems}</ul>;
  }

  renderDataPoint(header, data, large) {
    const classes = classnames('ndx-ProductSummary-dataPoint', {
      'ndx-ProductSummary-dataPoint--large': large
    });
    return (
      <div className={classes}>
        <Typography variant="heading5">{header}</Typography>
        <Typography variant="paragraphDefault">{data}</Typography>
      </div>
    );
  }

  renderSizing() {
    const {colorSizes} = this.props;
    const {youth, adult} = groupSizes(colorSizes);
    return (
      <div className="ndx-ProductSummary-sizes">
        {youth && this.renderDataPoint('Youth Sizes', youth.join(' | '), true)}
        {adult &&
          this.renderDataPoint(
            'Adult Sizes',
            adult.map(conciseSizeName).join(' | '),
            true
          )}
      </div>
    );
  }

  renderShippingInfo(styleId, turntimes) {
    const {standard, rush} = turntimes;
    return (
      <Fetch
        key={styleId}
        url={`${
          remoteServices.pricing
        }/guaranteed_by.json?styleno=${normalizeId(styleId)}`}>
        {data => (
          <div>
            {standard &&
              this.renderDataPoint(
                `${turnTimeInWords(standard)} ${STANDARD} Delivery`,
                `Get it ${data ? `by ${formatDate(data.standard)}` : ''}`
              )}
            {rush &&
              this.renderDataPoint(
                `${turnTimeInWords(rush)} ${RUSH} Delivery`,
                `Get it ${data ? `by ${formatDate(data.rush)}` : ''}`
              )}
          </div>
        )}
      </Fetch>
    );
  }

  renderSupportingImages(images) {
    const handleSelection = this.props.meta
      ? index => {
          const {
            product: {id: styleId}
          } = this.props;
          this.props.meta.productSummaryCarousel({
            styleId,
            imageOrdinal: index + 1
          });
        }
      : null;

    const title = 'Additional Product Views';
    const imageComponent = (
      <ImageViewer
        images={images}
        title={breakpoint(MediaQueries.mediumAndBelow) ? undefined : title}
        onSelectionChange={handleSelection}
      />
    );
    return breakpoint(MediaQueries.mediumAndBelow) ? (
      <DetailsToggle
        className="ndx-ProductSummary-carouselDetails"
        title={title}>
        {imageComponent}
      </DetailsToggle>
    ) : (
      imageComponent
    );
  }

  renderAdditionalPrintAreas(area) {
    return (
      <div className="ndx-ProductSummary-additionalPrintAreas">
        {(area === 'sleeve' || area === 'hood') &&
          this.renderSleevePrintArea('sleeve')}
        {area === 'hood' && this.renderHoodPrintArea('hood')}
        {area === 'leg' && this.renderLegPrintArea('leg')}
      </div>
    );
  }

  renderSleevePrintArea() {
    return (
      <div className="ndx-ProductSummary-additionalPrintArea">
        <div className="ndx-ProductSummary-additionalPrintAreaHeader">
          Sleeve Print
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaCopy">
          For orders of 6 or more, we offer four different styles for sleeve
          print designs.
        </div>
        <div
          className="ndx-ProductSummary-additionalPrintAreaLink"
          onClick={() => this.handleAdditionalPrintAreasClick('sleeve')}>
          <span>How to Add Sleeve Print</span>
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaThumbnails">
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={SLEEVE_PRINT.standard}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Standard
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={SLEEVE_PRINT.stacked}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Stacked
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={SLEEVE_PRINT.up}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Sentenced Up
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={SLEEVE_PRINT.down}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Sentenced Down
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  renderHoodPrintArea() {
    return (
      <div className="ndx-ProductSummary-additionalPrintArea">
        <div className="ndx-ProductSummary-additionalPrintAreaHeader">
          Hood Print
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaCopy">
          For orders of 6 or more, we offer three different styles for hood
          print designs.
        </div>
        <div
          className="ndx-ProductSummary-additionalPrintAreaLink"
          onClick={() => this.handleAdditionalPrintAreasClick('hood')}>
          <span>How to Add Hood Print</span>
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaThumbnails">
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={HOOD_PRINT.standard}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Standard
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={HOOD_PRINT.side}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Side Hood Up
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={HOOD_PRINT.readable}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Readable Hood Up
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail" />
        </div>
      </div>
    );
  }

  renderLegPrintArea() {
    return (
      <div className="ndx-ProductSummary-additionalPrintArea">
        <div className="ndx-ProductSummary-additionalPrintAreaHeader">
          Leg Print
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaCopy">
          For orders of 6 or more, we offer three different styles for leg print
          designs.
        </div>
        <div
          className="ndx-ProductSummary-additionalPrintAreaLink"
          onClick={() => this.handleAdditionalPrintAreasClick('leg')}>
          <span>How to Add Leg Print</span>
        </div>
        <div className="ndx-ProductSummary-additionalPrintAreaThumbnails">
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={LEG_PRINT.stacked}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Stacked
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={LEG_PRINT.up}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Sentenced Up
            </Typography>
          </div>
          <div className="ndx-ProductSummary-additionalPrintAreaThumbnail">
            <img
              className="ndx-ProductSummary-additionalPrintAreaThumbnailImage"
              src={LEG_PRINT.down}
            />
            <Typography
              variant="uiTextTiny"
              className="ndx-ProductSummary-additionalPrintAreaThumbnailLabel">
              Sentenced Down
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  handleAdditionalPrintAreasClick(area) {
    return this.props.largeBreakPoint
      ? this.props.handleAdditionalPrintAreasClick(area)
      : this.props.router.navigate(`/additionalPrintArea/${area}`);
  }

  renderContent() {
    const {product} = this.state;
    if (!product) return null;
    const isFundraisingMode = this.props.isFundraisingMode;
    const {
      additional_print_areas: additionalPrintArea,
      name,
      general_comments: description,
      minimum_quantity: minimum,
      subheading,
      sizing,
      turntime,
      material,
      features,
      supporting_images_fingerprinted: images,
      has_lineup: hasLineup,
      id: styleId
    } = product;
    const allowAdditionalPrintAreas = additionalPrintArea && !isFundraisingMode;

    return (
      <div className="ndx-ProductSummary">
        <article className="ndx-ProductSummary-copyWrapper">
          <Typography
            variant="heading3"
            dangerouslySetInnerHTML={renderNonBreakingHypenString(name)}
          />
          <section className="ndx-ProductSummary-section">
            <Typography
              variant="paragraphDefault"
              className="ndx-ProductSummary-desc"
              dangerouslySetInnerHTML={{__html: `${subheading} ${description}`}}
            />
          </section>
          <DetailsToggle
            className="ndx-ProductSummary-section"
            defaultExpanded
            title="Size & Fit Guide"
            onChange={({expanded}) =>
              this.handleDetailsAnalytics('sizeandfit', expanded)
            }>
            {this.renderSizing()}
            <div className="ndx-ProductSummary-fit">
              {this.renderDataPoint('Fit:', sizing, true)}
              {hasLineup && [
                <img key="img" alt="sizing lineup icon" src={SIZING} />,
                <PopupLink
                  key="link"
                  href={`/items/sizing/${normalizeId(
                    styleId
                  )}_lineup/standard.htm`}
                  options="width=640height,height=700,scrollbars=yes,toolbar=yes,menubar=no,location=yes,resizable=yes"
                  popupName={`CI_Size_Chart_${styleId}`}
                  tabIndex="-1">
                  View Sizing Guide
                </PopupLink>
              ]}
            </div>
          </DetailsToggle>
          {!isFundraisingMode && (
            <DetailsToggle
              className="ndx-ProductSummary-section"
              title="Shipping"
              onChange={({expanded}) =>
                this.handleDetailsAnalytics('shipping', expanded)
              }>
              {this.renderShippingInfo(styleId, turntime)}
              <div className="ndx-ProductSummary-shipping">
                <Typography
                  variant="heading3"
                  className="u-color-orange u-weight-light">
                  Free Shipping
                </Typography>
                <img
                  alt="The box that CustomInk products are shipped in"
                  className="ndx-ProductSummary-sectionImage"
                  src={SHIPPING_BOX}
                />
              </div>
            </DetailsToggle>
          )}
          <DetailsToggle
            className="ndx-ProductSummary-section"
            title="More Details"
            onChange={({expanded}) =>
              this.handleDetailsAnalytics('moredetails', expanded)
            }>
            {this.renderListItems(
              this.decorationDescription(product),
              material,
              features,
              `* Minimum Quantity: ${minimum}`
            )}
          </DetailsToggle>
          {allowAdditionalPrintAreas && (
            <DetailsToggle
              className="ndx-ProductSummary-section"
              title="Special Print Areas"
              onChange={({expanded}) =>
                this.handleDetailsAnalytics('additionalprintareas', expanded)
              }>
              {this.renderAdditionalPrintAreas(additionalPrintArea)}
            </DetailsToggle>
          )}
        </article>
        {this.renderSupportingImages(images)}
      </div>
    );
  }

  render() {
    const {isDisabled} = this.props;
    const {product} = this.state;
    if (isDisabled || !product) return null;
    if (this.props.largeBreakPoint) {
      return this.renderContent();
    } else {
      return (
        <ContentCard
          title="Product Details"
          onNavBack={() => this.props.router.navigate('/productDetails')}
          onNavClose={() => this.props.router.navigate('/')}>
          {this.renderContent()}
        </ContentCard>
      );
    }
  }
}

export default connect(select(), actions)(withRouter(ProductSummary));
